import React, { useEffect, useRef } from 'react';
interface AutoVideoProps {
  src: string;
  className?: string;
}
/**
 * A background video that stays loaded and keeps playing continuously.
 *
 * Reliability notes:
 * - React sets `muted` as a DOM *property*, and some browsers evaluate their
 *   autoplay policy before it's applied, blocking playback and leaving the
 *   video stuck on a spinner. We force the muted flag via a ref.
 * - Browsers (and OS power-saving) can quietly pause background/looping video
 *   on tab-switch, scroll, or network hiccups. We listen for every event that
 *   signals a stop (`pause`, `stalled`, `waiting`, `ended`, tab visibility)
 *   and immediately resume so the video never appears frozen.
 */
export const AutoVideo = ({
  src,
  className
}: AutoVideoProps) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    // Force the muted state as a real property so autoplay is allowed.
    video.muted = true;
    video.defaultMuted = true;
    const resume = () => {
      if (video.paused || video.ended) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    };
    // Kick playback on mount and on every readiness/stop signal.
    resume();
    const events = ['canplay', 'loadeddata', 'pause', 'stalled', 'waiting', 'ended'];
    events.forEach(e => video.addEventListener(e, resume));
    // Resume when the tab/window regains focus.
    const onVisible = () => {
      if (!document.hidden) resume();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', resume);
    return () => {
      events.forEach(e => video.removeEventListener(e, resume));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', resume);
    };
  }, [src]);
  return <video ref={ref} src={src} autoPlay loop muted playsInline preload="auto" className={className} data-id="element-40" data-mp="145jm12gmaao1" />;
};